import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { GeneralTab } from './tabs/GeneralTab';
import { MembersTab } from './tabs/MembersTab';
import { OutcomesTab } from './tabs/OutcomesTab';
import { BillingTab } from './tabs/BillingTab';
import { CreditsTab } from './tabs/CreditsTab';
import { RecommendationTab } from './tabs/RecommendationTab';
import { AvailabilityTab } from './tabs/AvailabilityTab';
import { MyAvailabilityTab } from './tabs/MyAvailabilityTab';

export type SettingsTab =
  | 'general'
  | 'members'
  | 'availability'
  | 'my-availability'
  | 'outcomes'
  | 'billing'
  | 'credits'
  | 'recommendation';

const VALID_TABS: SettingsTab[] = [
  'general',
  'members',
  'availability',
  'my-availability',
  'outcomes',
  'billing',
  'credits',
  'recommendation',
];

interface TabConfig {
  key: SettingsTab;
  label: string;
  icon: JSX.Element;
}

const tabs: TabConfig[] = [
  {
    key: 'general',
    label: 'General',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    key: 'members',
    label: 'Members',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
  {
    // MEMBER self-service. Deliberately a separate entry from the administrator
    // "Availability" tab below: same domain, same endpoints, same validator —
    // the only difference is that this one targets the signed-in member's own
    // membership. It is NOT appointment/customer scheduling, which is a
    // different bounded context that merely shares the word "schedule".
    key: 'my-availability',
    label: 'My Work Availability',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    // Central Availability — workspace attendance policy + member working hours.
    // Owner/admin managed; the API enforces it and the tab reports it.
    key: 'availability',
    label: 'Availability',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'outcomes',
    label: 'Outcomes',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    key: 'credits',
    label: 'Credits',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    // PR-D7 — advisory capacity recommendation surface. Read-only;
    // shows the latest recommendation computed by the D7 scheduler
    // (or falls back to on-demand compute via the D5b endpoint).
    key: 'recommendation',
    label: 'Recommendation',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

interface WorkspaceInfo {
  id: string;
  name: string;
  type: 'personal' | 'team' | 'enterprise';
  status: string;
}

/**
 * Workspace Settings page
 *
 * Central-hosted settings with workspace-scoped routing.
 * Routes: /workspace/:workspaceId/settings/:tab
 * Supports returnUrl for back navigation.
 */
export function WorkspaceSettingsPage() {
  const { workspaceId: urlWorkspaceId, tab: urlTab, action: _urlAction } = useParams<{
    workspaceId?: string;
    tab?: string;
    action?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<WorkspaceInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // If navigating from an external app (return_to/returnUrl present), always
  // use the session bridge for a fresh token — don't trust stale localStorage.
  const hasExternalReturn = searchParams.has('return_to') || searchParams.has('returnUrl');
  const [token, setToken] = useState<string | null>(
    hasExternalReturn ? null : localStorage.getItem('admin_token')
  );

  // Map URL tab names to canonical settings tabs.
  // BrandTalk uses: /settings/profile, /settings/workspace, /settings/workspace/transfer
  // Central uses: general, members, outcomes, billing, credits
  const TAB_ALIASES: Record<string, SettingsTab> = {
    profile: 'general',
    workspace: 'general',
    account: 'general',
  };

  const resolvedTab = urlTab
    ? TAB_ALIASES[urlTab] || (VALID_TABS.includes(urlTab as SettingsTab) ? urlTab as SettingsTab : 'general')
    : (searchParams.get('tab') as SettingsTab) || 'general';
  const activeTab: SettingsTab = resolvedTab;
  const returnUrl = searchParams.get('returnUrl') || searchParams.get('return_to') || '';

  // Load workspace info and handle routing
  useEffect(() => {
    async function loadWorkspace() {
      try {
        // If no token, attempt session bridge (cookie → token)
        let authToken = token;
        if (!authToken) {
          const bridgeRes = await fetch('/api/auth/session/bridge', {
            credentials: 'include',
          });
          if (bridgeRes.ok) {
            const bridgeData = await bridgeRes.json();
            // Multiple workspaces and no current selection: the bridge does
            // NOT bind an arbitrary (oldest) workspace — it asks us to route
            // the user to the workspace picker instead of silently editing the
            // wrong workspace.
            if (bridgeData.needsWorkspaceSelection || !bridgeData.accessToken) {
              const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
              window.location.href = `/auth/workspace?continue=${returnTo}`;
              return;
            }
            authToken = bridgeData.accessToken;
            localStorage.setItem('admin_token', authToken!);
            setToken(authToken);
            // Don't continue — setToken will re-trigger this effect with the token set
            return;
          } else {
            // No session — redirect to OIDC login, then return here
            const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = `/auth/login?continue=${returnTo}`;
            return;
          }
        }

        let wsId = urlWorkspaceId;

        // Legacy id-less route (no workspace ID in the URL): resolve the
        // user's CURRENT authoritative workspace from the session — NOT
        // workspaces[0] (arbitrary/oldest). /api/workspace/current is the
        // canonical server-side "which workspace am I in" rule, so the id-less
        // path lands on the same workspace the user is actually active in
        // (Defect B: no silent fallback to the oldest/personal workspace).
        if (!wsId) {
          const currentRes = await fetch('/api/workspace/current', {
            credentials: 'include',
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (currentRes.ok) {
            const currentData = await currentRes.json();
            wsId = currentData.workspace?.id;
          }

          if (wsId) {
            const tab = searchParams.get('tab') || 'general';
            const tabPath = tab === 'general' ? '' : `/${tab}`;
            const returnParam = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
            navigate(`/workspace/${wsId}/settings${tabPath}${returnParam}`, { replace: true });
            return;
          }

          // No workspace bound to the session — route the user to choose one
          // rather than silently editing an arbitrary/oldest workspace.
          setError('No active workspace selected. Please choose a workspace to edit.');
          setIsLoading(false);
          return;
        }

        // Validate workspace access by fetching workspace details
        const wsRes = await fetch(`/api/workspaces/${wsId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!wsRes.ok) {
          if (wsRes.status === 401) {
            // Token expired — clear and retry via bridge
            localStorage.removeItem('admin_token');
            setToken(null);
            return;
          }
          if (wsRes.status === 404 || wsRes.status === 403) {
            setError('Workspace not found or access denied');
          } else {
            setError('Failed to load workspace');
          }
          setIsLoading(false);
          return;
        }

        const wsData = await wsRes.json();
        setWorkspace(wsData.workspace);
        setIsLoading(false);
      } catch {
        setError('Failed to load workspace');
        setIsLoading(false);
      }
    }

    loadWorkspace();
  }, [urlWorkspaceId, navigate, searchParams, returnUrl, token]);

  const handleTabChange = (tab: SettingsTab) => {
    if (!workspace) return;
    const tabPath = tab === 'general' ? '' : `/${tab}`;
    const returnParam = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
    navigate(`/workspace/${workspace.id}/settings${tabPath}${returnParam}`);
  };

  const handleBack = () => {
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      navigate('/');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-900 font-medium">{error}</p>
          <button
            onClick={handleBack}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Go back"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Workspace Settings</h1>
                {workspace && (
                  <p className="text-sm text-gray-500">{workspace.name}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar tabs */}
          <nav className="lg:w-56 flex-shrink-0">
            <ul className="space-y-1">
              {tabs.map((tab) => (
                <li key={tab.key}>
                  <button
                    onClick={() => handleTabChange(tab.key)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.key
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <span className={activeTab === tab.key ? 'text-blue-600' : 'text-gray-400'}>
                      {tab.icon}
                    </span>
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Tab content */}
          <div className="flex-1 min-w-0">
            {activeTab === 'general' && workspace && (
              <GeneralTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'members' && workspace && (
              <MembersTab workspaceId={workspace.id} workspaceType={workspace.type} authToken={token!} />
            )}
            {activeTab === 'my-availability' && workspace && (
              <MyAvailabilityTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'availability' && workspace && (
              <AvailabilityTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'outcomes' && workspace && (
              <OutcomesTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'billing' && workspace && (
              <BillingTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'credits' && workspace && (
              <CreditsTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'recommendation' && workspace && (
              <RecommendationTab workspaceId={workspace.id} authToken={token!} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
